import React, { useCallback, useMemo, useState } from 'react';
import { Button, Modal, Space, Tag, Typography } from 'antd';
import { toast } from '../../../lib/toast';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../../../components/DataTable';
import { COG_ACTIONS_COLUMN_WIDTH, ListPage } from '../../../components/list';
import Can from '../../permissions/Can';
import HostTenantRowActions from './HostTenantRowActions';
import type { HostTenantListItem, HostTenantStatus, TenantLicenseRequest } from '../../tenants/api/tenants';
import { normalizeTenantStatus } from '../../tenants/api/tenants';
import {
  useApproveHostTenant,
  useCreateHostTenant,
  useExpireHostTenant,
  useResendTenantOnboarding,
  useHostDashboard,
  useHostTenants,
  useRejectHostTenant,
  useUpdateHostTenant,
  useUpdateTenantLicense,
} from '../hooks/useHostConsole';
import { useEnterTenantSupportView } from '../hooks/useSupportView';
import { useAuthStore } from '../../auth/store/authStore';
import ApproveHostTenantModal from './ApproveHostTenantModal';
import CreateHostTenantDrawer from './CreateHostTenantDrawer';
import EditHostTenantDrawer from './EditHostTenantDrawer';
import EditTenantLicenseModal from './EditTenantLicenseModal';
import HostTenantFilterBar, { type HostTenantFilterValues } from './HostTenantFilterBar';
import HostTenantStats from './HostTenantStats';
import {
  formatTenantDate,
  licenseDisplayState,
  matchesLicenseFilter,
  primaryContactName,
  tenantStatusLabel,
  tenantStatusTagColor,
} from '../utils/tenantStatus';

const { Text } = Typography;

const defaultFilter: HostTenantFilterValues = {
  search: undefined,
  status: undefined,
  license: 'all',
};

const HostConsolePage: React.FC = () => {
  const [filter, setFilter] = useState<HostTenantFilterValues>(defaultFilter);
  const [createOpen, setCreateOpen] = useState(false);
  const [approveTenant, setApproveTenant] = useState<HostTenantListItem | null>(null);
  const [licenseTenant, setLicenseTenant] = useState<HostTenantListItem | null>(null);
  const [editTenant, setEditTenant] = useState<HostTenantListItem | null>(null);

  const { data: dashboard, isLoading: dashboardLoading } = useHostDashboard();
  const { data: tenants = [], isLoading: tenantsLoading } = useHostTenants(filter.status);

  const approveMutation = useApproveHostTenant();
  const rejectMutation = useRejectHostTenant();
  const expireMutation = useExpireHostTenant();
  const updateLicenseMutation = useUpdateTenantLicense(() => {
    setLicenseTenant(null);
    toast.success('License end date updated.');
  });
  const updateTenantMutation = useUpdateHostTenant(() => {
    setEditTenant(null);
    toast.success('Tenant updated.');
  });
  const resendOnboardingMutation = useResendTenantOnboarding();
  const createMutation = useCreateHostTenant(() => {
    setCreateOpen(false);
    toast.success('Tenant created. Approve when ready to send an invite.');
  });
  const isHostUser = useAuthStore((state) => Boolean(state.user?.isHost));
  const enterSupportViewMutation = useEnterTenantSupportView();

  const handleViewCompany = useCallback((tenant: HostTenantListItem) => {
    Modal.confirm({
      title: 'Switch to this company?',
      content: `You will operate in ${tenant.companyName} as a host operator. Changes you make are live.`,
      okText: 'Switch company',
      onOk: () => enterSupportViewMutation.mutateAsync(tenant.id),
    });
  }, [enterSupportViewMutation]);

  if (!isHostUser) {
    return null;
  }

  const handleCreateTenant = useCallback((values: Parameters<typeof createMutation.mutate>[0]) => {
    createMutation.mutate(values, {
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Unable to create tenant.');
      },
    });
  }, [createMutation]);

  const filteredTenants = useMemo(() => {
    const search = filter.search?.trim().toLowerCase();

    return tenants.filter((row) => {
      if (!matchesLicenseFilter(row, filter.license)) {
        return false;
      }

      if (!search) {
        return true;
      }

      const haystack = [
        row.companyName,
        row.primaryContactEmail,
        row.primaryContactFirstName,
        row.primaryContactLastName,
        primaryContactName(row),
        row.phone ?? '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [tenants, filter.license, filter.search]);

  const handleApproveSubmit = useCallback((tenantId: string, license: TenantLicenseRequest) => {
    approveMutation.mutate(
      { tenantId, license },
      {
        onSuccess: () => {
          setApproveTenant(null);
          toast.success('Tenant approved and invite sent.');
        },
        onError: (error: unknown) => {
          toast.error(error instanceof Error ? error.message : 'Failed to approve tenant.');
        },
      },
    );
  }, [approveMutation]);

  const handleResendOnboarding = useCallback((row: HostTenantListItem) => {
    Modal.confirm({
      title: 'Resend activation email?',
      content: `Send a new set-password link to ${row.primaryContactEmail}?`,
      okText: 'Send email',
      onOk: () =>
        resendOnboardingMutation
          .mutateAsync(row.id)
          .then(() => toast.success('Activation email sent.'))
          .catch((error: unknown) => {
            const detail = error instanceof Error ? error.message : 'Could not resend activation email.';
            toast.error(detail);
            throw error;
          }),
    });
  }, [resendOnboardingMutation]);

  const handleReject = useCallback((row: HostTenantListItem) => {
    Modal.confirm({
      title: 'Reject signup?',
      content: `Decline the request from ${row.companyName}?`,
      okText: 'Reject',
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          rejectMutation.mutate(row.id, {
            onSuccess: () => {
              toast.success('Tenant rejected.');
              resolve();
            },
            onError: (error) => {
              const detail = error instanceof Error ? error.message : 'Failed to reject tenant.';
              toast.error(detail);
              reject(new Error(detail));
            },
          });
        }),
    });
  }, [rejectMutation]);

  const handleExpire = useCallback((row: HostTenantListItem) => {
    Modal.confirm({
      title: 'Expire tenant?',
      content: `Users for ${row.companyName} will no longer be able to sign in.`,
      okText: 'Expire',
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          expireMutation.mutate(row.id, {
            onSuccess: () => {
              toast.success('Tenant expired.');
              resolve();
            },
            onError: () => {
              toast.error('Failed to expire tenant.');
              reject(new Error('Failed'));
            },
          });
        }),
    });
  }, [expireMutation]);

  const handleStatClick = useCallback((status?: HostTenantStatus) => {
    setFilter((current) => ({ ...current, status, license: 'all' }));
  }, []);

  const handleExpiringStatClick = useCallback(() => {
    setFilter((current) => ({
      ...current,
      status: 'Active',
      license: 'expiring',
    }));
  }, []);

  const columns: ColumnsType<HostTenantListItem> = useMemo(
    () => [
      {
        title: 'Company',
        dataIndex: 'companyName',
        key: 'companyName',
        render: (value: string) => <Typography.Text strong>{value}</Typography.Text>,
      },
      {
        title: 'Primary contact',
        key: 'contact',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Text>{primaryContactName(row)}</Text>
            <Text type="secondary">{row.primaryContactEmail}</Text>
          </Space>
        ),
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        render: (value?: string) => value ?? <Text type="secondary">—</Text>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: HostTenantStatus | number | string, row) => {
          const normalized = normalizeTenantStatus(status);
          return (
            <Space size={4} wrap>
              <Tag color={tenantStatusTagColor(normalized)}>{tenantStatusLabel(normalized)}</Tag>
              {normalized === 'Approved' && row.primaryContactAccountStatus === 'Pending' && (
                <Tag color="default">Invite pending</Tag>
              )}
            </Space>
          );
        },
      },
      {
        title: 'License ends',
        dataIndex: 'licenseEndsAtUtc',
        key: 'licenseEndsAtUtc',
        render: (_: string | undefined, row) => {
          const state = licenseDisplayState(row);

          if (state === 'none') {
            return <Text type="secondary">—</Text>;
          }

          return (
            <Space direction="vertical" size={0}>
              <Text>{formatTenantDate(row.licenseEndsAtUtc)}</Text>
              {state === 'expiring' && <Tag color="warning">Expiring soon</Tag>}
              {state === 'overdue' && <Tag color="error">Overdue</Tag>}
            </Space>
          );
        },
      },
      {
        title: 'Created',
        dataIndex: 'createdAtUtc',
        key: 'createdAtUtc',
        render: (value: string) => formatTenantDate(value),
      },
      {
        title: '#',
        key: 'actions',
        width: COG_ACTIONS_COLUMN_WIDTH,
        fixed: 'right',
        align: 'center',
        render: (_: unknown, row: HostTenantListItem, index: number) => (
          <HostTenantRowActions
            record={row}
            rowNumber={index + 1}
            onEdit={setEditTenant}
            onApprove={setApproveTenant}
            onReject={handleReject}
            onResendOnboarding={handleResendOnboarding}
            onLicense={setLicenseTenant}
            onExpire={handleExpire}
            onViewCompany={handleViewCompany}
          />
        ),
      },
    ],
    [handleExpire, handleReject, handleResendOnboarding],
  );

  return (
    <>
      <ListPage
        title="Tenant console"
        subtitle="Review signups, create organizations, and manage tenant lifecycle."
        header={(
          <HostTenantStats
            dashboard={dashboard}
            loading={dashboardLoading}
            activeStatus={filter.status}
            onStatusClick={handleStatClick}
            onExpiringClick={handleExpiringStatClick}
            activeLicenseFilter={filter.license}
          />
        )}
        actions={(
          <Can permission="host.tenants.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              Create tenant
            </Button>
          </Can>
        )}
        filters={(
          <HostTenantFilterBar
            value={filter}
            onChange={setFilter}
            onReset={() => setFilter(defaultFilter)}
          />
        )}
      >
        <DataTable<HostTenantListItem>
          columns={columns}
          dataSource={filteredTenants}
          loading={tenantsLoading || dashboardLoading}
          rowKey="id"
          bordered
          striped
          emptyText="No tenants match the current filters."
        />
      </ListPage>

      <CreateHostTenantDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreateTenant}
        saving={createMutation.isPending}
      />

      <ApproveHostTenantModal
        tenant={approveTenant}
        open={approveTenant !== null}
        saving={approveMutation.isPending}
        onClose={() => setApproveTenant(null)}
        onApprove={handleApproveSubmit}
      />

      <EditTenantLicenseModal
        tenant={licenseTenant}
        open={licenseTenant !== null}
        saving={updateLicenseMutation.isPending}
        onClose={() => setLicenseTenant(null)}
        onSave={(tenantId, licenseEndsAtUtc) => {
          updateLicenseMutation.mutate(
            { tenantId, licenseEndsAtUtc },
            { onError: () => toast.error('Failed to update license.') },
          );
        }}
      />

      <EditHostTenantDrawer
        tenant={editTenant}
        open={editTenant !== null}
        saving={updateTenantMutation.isPending}
        onClose={() => setEditTenant(null)}
        onSave={(tenantId, values) => {
          updateTenantMutation.mutate(
            { tenantId, request: values },
            { onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to update tenant.') },
          );
        }}
      />
    </>
  );
};

export default HostConsolePage;
